import { inject, injectable } from 'tsyringe';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { RecommendationConditions } from '../../datastore-service/models/conditions.model';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { LoggerService } from '../../logger.service';
import { kDITOWExtensionWrapper } from '../../../constants/injectionTokens';
import { IOWExtensionWrapper } from '../../../interfaces/ow-wrappers/ow-extension-wrapper.interface';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.AppSpecific)
export class ConditionDedicatedAppInstalled implements ICondition {
  private readonly logger = LoggerService.getCategory(
    'ConditionDedicatedAppInstalled',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITOWExtensionWrapper)
    private readonly owExtensionWrapper: IOWExtensionWrapper,
  ) {}

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const downloadedExtensions =
      await this.owExtensionWrapper.getInstalledAndEnabledExtensions();

    if (!downloadedExtensions) {
      return [true];
    }

    const installedDedicatedApps = downloadedExtensions.filter((app) => {
      return (
        app.type === 'WebApp' &&
        app.data.game_targeting &&
        app.data.game_targeting.type === 'dedicated'
      );
    });

    const { game } = context;
    const gameId = game.targetGameId ? game.targetGameId : game.id;

    for (const app of installedDedicatedApps) {
      if (app.data.game_targeting.game_ids.includes(gameId)) {
        this.logger.info(
          `Dedicated app for game: ${game.name} is already installed`,
        );
        return [false, NoRecommendationsReasons.DedicatedAppAlreadyInstalled];
      }
    }

    return [true];
  }

  // ---------------------------------------------------------------------------
  async shouldSkip(context: ConditionContext): Promise<boolean> {
    const { application } = context;

    if (!application.appConditions) {
      return true;
    }

    const hasCondition = application.appConditions.includes(
      RecommendationConditions.ConditionNoInstalledDedicatedGameApplication,
    );

    return !hasCondition;
  }
}
